import React, { useEffect, useMemo, useState } from "react";
import { addDoc, collection, deleteDoc, doc, onSnapshot, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import SchedulingPopup from "./SchedulingPopup";
import {
  mergeSchedulingEmployees,
  readSchedulingEmployees,
  readSchedulingOwner,
  readSchedulingSchemas,
} from "../utils/schedulingPopupData";

export default function ConnectedSchedulingPopup({
  isOpen,
  onClose,
  companyUid,
  role,
  allowedSchemaIds,
  permissionsLoading,
  onSchemaCreated,
  onUnassignedCountChange,
}) {
  const [schemas, setSchemas] = useState([]);
  const [employedEmployees, setEmployedEmployees] = useState([]);
  const [ownerEmployee, setOwnerEmployee] = useState(null);
  const [loaded, setLoaded] = useState({ schemas: false, employees: false, owner: false });
  const [loadError, setLoadError] = useState("");
  const shouldSubscribe = isOpen || !!onUnassignedCountChange;

  useEffect(() => {
    onUnassignedCountChange?.(0);
    if (!shouldSubscribe || !companyUid) return;
    setSchemas([]);
    setEmployedEmployees([]);
    setOwnerEmployee(null);
    setLoaded({ schemas: false, employees: false, owner: false });
    setLoadError("");

    const onError = (error) => {
      console.error("Kunde inte hämta schemapopupens data:", error);
      setLoadError("Kunde inte hämta scheman och användare. Stäng popupen och försök igen.");
    };
    const unsubscribeSchemas = onSnapshot(collection(db, "users", companyUid, "Scheduling"),
      (snapshot) => {
        setSchemas(readSchedulingSchemas(snapshot));
        setLoaded((previous) => ({ ...previous, schemas: true }));
      }, onError);
    const unsubscribeEmployees = onSnapshot(collection(db, "users", companyUid, "employed"),
      (snapshot) => {
        setEmployedEmployees(readSchedulingEmployees(snapshot));
        setLoaded((previous) => ({ ...previous, employees: true }));
      }, onError);
    const unsubscribeOwner = onSnapshot(doc(db, "users", companyUid),
      (snapshot) => {
        setOwnerEmployee(readSchedulingOwner(snapshot));
        setLoaded((previous) => ({ ...previous, owner: true }));
      }, onError);

    return () => {
      unsubscribeSchemas();
      unsubscribeEmployees();
      unsubscribeOwner();
    };
  }, [shouldSubscribe, companyUid, onUnassignedCountChange]);

  const employees = useMemo(() => mergeSchedulingEmployees(employedEmployees, ownerEmployee),
    [employedEmployees, ownerEmployee]);
  const availableSchemas = useMemo(() => {
    if (role === "admin" && permissionsLoading) return [];
    return Array.isArray(allowedSchemaIds)
      ? schemas.filter((schema) => allowedSchemaIds.includes(schema.id)) : schemas;
  }, [schemas, role, permissionsLoading, allowedSchemaIds]);
  const loading = !loaded.schemas || !loaded.employees || !loaded.owner ||
    (role === "admin" && permissionsLoading);

  useEffect(() => {
    if (!companyUid || loading || loadError) {
      onUnassignedCountChange?.(0);
      return;
    }
    const assignedIds = new Set(schemas.flatMap((schema) => schema.visibleEmployeeIds || []));
    onUnassignedCountChange?.(employees.filter((employee) => !assignedIds.has(employee.id)).length);
  }, [companyUid, loading, loadError, schemas, employees, onUnassignedCountChange]);

  const createSchema = async (name) => {
    const trimmedName = name.trim();
    if (!companyUid || loading || loadError || !trimmedName) return false;
    if (schemas.some((schema) => schema.name.toLowerCase() === trimmedName.toLowerCase())) {
      alert("Det finns redan ett schema med det namnet.");
      return false;
    }
    try {
      const schemaRef = await addDoc(collection(db, "users", companyUid, "Scheduling"), {
        name: trimmedName,
        visibleEmployeeIds: [],
        createdAt: serverTimestamp(),
      });
      setSchemas((previous) => previous.some((schema) => schema.id === schemaRef.id)
        ? previous
        : [...previous, { id: schemaRef.id, name: trimmedName, visibleEmployeeIds: [] }]
          .sort((a, b) => a.name.localeCompare(b.name, "sv")));
      onSchemaCreated?.(schemaRef.id, trimmedName);
      return true;
    } catch (error) {
      console.error("Kunde inte skapa schema:", error);
      alert("Kunde inte skapa schemat. Försök igen.");
      return false;
    }
  };

  const requireSchema = (schemaId) => {
    if (!companyUid || !availableSchemas.some((schema) => schema.id === schemaId)) {
      throw new Error("Schemat är inte längre tillgängligt.");
    }
  };

  const deleteSchema = async (schemaId) => {
    requireSchema(schemaId);
    await deleteDoc(doc(db, "users", companyUid, "Scheduling", schemaId));
    setSchemas((previous) => previous.filter((schema) => schema.id !== schemaId));
  };

  const renameSchema = async (schemaId, name) => {
    requireSchema(schemaId);
    const trimmedName = name.trim();
    if (!trimmedName) throw new Error("Ange ett namn på schemat.");
    if (schemas.some((schema) => schema.id !== schemaId && schema.name.toLowerCase() === trimmedName.toLowerCase())) {
      throw new Error("Det finns redan ett schema med det namnet.");
    }
    await updateDoc(doc(db, "users", companyUid, "Scheduling", schemaId), {
      name: trimmedName,
      updatedAt: serverTimestamp(),
    });
    setSchemas((previous) => previous.map((schema) => schema.id === schemaId
      ? { ...schema, name: trimmedName } : schema).sort((a, b) => a.name.localeCompare(b.name, "sv")));
  };

  const saveSchemaEmployees = async (schemaId, employeeIds) => {
    requireSchema(schemaId);
    await updateDoc(doc(db, "users", companyUid, "Scheduling", schemaId), {
      visibleEmployeeIds: employeeIds,
      updatedAt: serverTimestamp(),
    });
    setSchemas((previous) => previous.map((schema) => schema.id === schemaId
      ? { ...schema, visibleEmployeeIds: employeeIds } : schema));
  };

  return <SchedulingPopup
    isOpen={isOpen}
    onClose={onClose}
    schemas={availableSchemas}
    allSchemas={schemas}
    employees={employees}
    loading={loading}
    loadError={loadError}
    onCreateSchema={createSchema}
    onDeleteSchema={deleteSchema}
    onRenameSchema={renameSchema}
    onSaveSchemaEmployees={saveSchemaEmployees}
  />;
}
